import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { OpsUser, OpsUserSummary } from '../../api/types';
import dialog from '../../components/ui/ConfirmDialog.module.css';
import { Button } from '../../components/ui/Button';
import { DialogShell } from '../../components/ui/DialogShell';
import { DialogHelp } from '../../help/DialogHelp';
import { useDialogDismiss, useIsDirty } from '../../components/ui/dialogDismiss';
import { opsUserName, REASON_MAX, validateReason } from './opsUser';
import styles from './OwnershipPanel.module.css';

interface Props {
  currentOwner: OpsUserSummary | null;
  opsUsers: OpsUser[];
  loading: boolean;
  errorMessage: string | null;
  onConfirm: (opsUserId: number, reason: string) => void;
  onCancel: () => void;
}

/** Manager-only: pick an ACTIVE ops user + a reason (ASSIGN / REASSIGN). */
export function ReassignModal({
  currentOwner,
  opsUsers,
  loading,
  errorMessage,
  onConfirm,
  onCancel,
}: Props) {
  const { t } = useTranslation();
  const [assigneeId, setAssigneeId] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const candidates = opsUsers.filter((u) => u.status === 'ACTIVE' && u.id !== currentOwner?.id);
  const title = currentOwner ? 'Reassign shipment' : 'Assign shipment';
  const dirty = useIsDirty({ assigneeId, reason });
  const dismiss = useDialogDismiss({ dirty, busy: loading, onClose: onCancel });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!assigneeId) {
      setError('Pick an ops user to assign.');
      return;
    }
    const problem = validateReason(reason);
    if (problem) {
      setError(problem);
      return;
    }
    setError(null);
    onConfirm(Number(assigneeId), reason.trim());
  };

  return (
    <DialogShell dismiss={dismiss} label={title} scrimClassName={dialog.backdrop} panelClassName={dialog.modal}>
      <div className={dialog.header}>
        <h3 className={dialog.title}>{title}</h3>
        <DialogHelp context="reassign-shipment" title={title} />
        <button
          type="button"
          className={dialog.closeBtn}
          onClick={dismiss.requestClose}
          disabled={loading}
          aria-label={t('common.actions.close')}
        >
          &times;
        </button>
      </div>
      <form onSubmit={submit} className={dialog.body}>
        <div className={dialog.description}>
          {currentOwner
            ? `Currently owned by ${opsUserName(currentOwner)}. Managers may exceed a person’s open-order cap.`
            : 'Nobody owns this shipment yet. Managers may exceed a person’s open-order cap.'}
        </div>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>{t('ownership.reassignModal.assignTo')}</span>
          <select
            className={styles.select}
            value={assigneeId}
            onChange={(e) => setAssigneeId(e.target.value)}
            disabled={loading}
          >
            <option value="">{t('ownership.reassignModal.selectPlaceholder')}</option>
            {candidates.map((u) => (
              <option key={u.id} value={u.id}>
                {t('ownership.reassignModal.candidateOption', {
                  name: opsUserName(u),
                  open: u.openOrderCount,
                  cap: u.effectiveMaxOpenOrders,
                })}
              </option>
            ))}
          </select>
        </label>
        <div className={dialog.reasonWrapper}>
          <textarea
            aria-label={t('ownership.reassignModal.reasonLabel')}
            className={`${dialog.textarea} ${error ? dialog.textareaError : ''}`}
            placeholder={t('ownership.reassignModal.reasonPlaceholder')}
            value={reason}
            maxLength={REASON_MAX}
            rows={3}
            disabled={loading}
            onChange={(e) => setReason(e.target.value)}
          />
          {error && <span className={dialog.errorText}>{error}</span>}
        </div>
        {errorMessage && (
          <span role="alert" className={dialog.errorText}>
            {errorMessage}
          </span>
        )}
        <div className={dialog.actions}>
          <Button variant="secondary" onClick={dismiss.requestClose} disabled={loading}>
            {t('common.actions.cancel')}
          </Button>
          <Button variant="primary" type="submit" busy={loading}>
            {loading ? 'Processing...' : currentOwner ? 'Reassign' : 'Assign'}
          </Button>
        </div>
      </form>
    </DialogShell>
  );
}
